/**
 * ================================================================================================
 *  THE PLATFORM'S OWN POSITION — COUNTED FROM THE ROWS THE SERVER SENT, AND NOTHING ELSE
 * ================================================================================================
 *
 * The Owner home needs figures. There are exactly two honest ways to put a number on a screen: read
 * it from the server, or compute it from rows the server sent and say which rows. This does the
 * second, and it is PURE so that the arithmetic can be tested without a browser.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ────────────────────────────────────────────────────────────
 *
 * There is no MRR here, no ARR, no revenue, no collection rate. `platform.tenant.read` answers with
 * `TenantAdminSummary` — name, slug, status, subscription state, company count, seat count — and
 * carries NO AMOUNT of any kind, deliberately: 08 §8.4 / CF-66 keep money out of entitlement, and
 * the read that the Owner home is built on was authorized without a price ever being seen.
 *
 * So a revenue tile here would be a number nothing produced. `FIGURES_NOT_AVAILABLE` names each one
 * that a person will look for, and says why it is absent — because "not shown" and "zero" are two
 * different facts about a business, and a dashboard that renders ₪0 for a platform that has never
 * been able to compute revenue is stating the wrong one.
 *
 * ── AND ZERO IS A REAL ANSWER ───────────────────────────────────────────────────────────────────
 *
 * A live platform with no customers yet has 0 customers, 0 companies, 0 seats and 0 trials, and
 * every one of those is TRUE. The screen renders them as an empty state in words; it does not
 * render a dash, and it does not withhold the figure until it becomes interesting.
 */

import type { SubscriptionState, TenantAdminSummary, TenantStatus } from './contract';

/** One bucket of the breakdown. `count` is a count of ROWS, never an estimate. */
export interface EstateBucket<T extends string> {
  readonly code: T;
  readonly count: number;
}

export interface EstateSummary {
  /** Rows in the answer. The denominator of every other figure here. */
  readonly customers: number;
  /** Customers whose SUBSCRIPTION is `active`. Not the same question as the tenant's status. */
  readonly activeSubscriptions: number;
  /** Customers currently in a trial — `subscriptionState === 'trialing'`. */
  readonly trials: number;
  /** Customers a commercial state is currently denying: `past_due`, `suspended` or `expired`. */
  readonly commerciallyBlocked: number;
  /**
   * Customers with NO LIVE SUBSCRIPTION AT ALL — provisioned, not yet sold.
   *
   * This read `draft` and said "no subscription row yet", which are two different facts: `draft` is
   * a row that exists and grants nothing, and a customer between step 1 and step 2 of provisioning
   * has no row whatsoever. The server now distinguishes them (`subscriptionState: null`), and this
   * figure counts the second — so the first customer on a new estate is reported, rather than
   * counted as zero everywhere and appearing in no figure at all.
   */
  readonly withoutSubscription: number;
  /** Sum of the server's own `companyCount`. Never recomputed from a company list. */
  readonly companies: number;
  /** Sum of the server's own `seatCount`. */
  readonly seats: number;
  /** Non-empty buckets only, largest first, so an empty platform shows an empty breakdown. */
  readonly byStatus: readonly EstateBucket<TenantStatus>[];
  readonly bySubscriptionState: readonly EstateBucket<SubscriptionState>[];
}

/**
 * The commercial states that DENY. 08 §6.0 D11: `suspended` denies every action class, and
 * `past_due` and `expired` are the two that arrive there without anyone deciding to suspend.
 */
const BLOCKING_STATES: ReadonlySet<SubscriptionState> = new Set<SubscriptionState>([
  'past_due',
  'suspended',
  'expired',
]);

function tally<T extends string>(codes: readonly T[]): readonly EstateBucket<T>[] {
  const counts = new Map<T, number>();
  for (const code of codes) counts.set(code, (counts.get(code) ?? 0) + 1);
  return [...counts.entries()]
    .map(([code, count]) => ({ code, count }))
    .sort((a, b) => (b.count - a.count === 0 ? a.code.localeCompare(b.code) : b.count - a.count));
}

/** Every figure on the Owner home, from one read. */
export function summariseEstate(tenants: readonly TenantAdminSummary[]): EstateSummary {
  let companies = 0;
  let seats = 0;
  for (const t of tenants) {
    companies += t.companyCount;
    seats += t.seatCount;
  }

  const states = tenants.map((t) => t.subscriptionState);

  return {
    customers: tenants.length,
    activeSubscriptions: states.filter((s) => s === 'active').length,
    trials: states.filter((s) => s === 'trialing').length,
    commerciallyBlocked: states.filter((s) => s !== null && BLOCKING_STATES.has(s)).length,
    withoutSubscription: states.filter((s) => s === null).length,
    companies,
    seats,
    byStatus: tally(tenants.map((t) => t.status)),
    /*
     * ONLY THE STATES THAT EXIST. A customer with no subscription is in `withoutSubscription`
     * above, not invented into a bucket here: `EstateBucket<SubscriptionState>` is typed to the
     * contract's closed set, and a display category inside it would be a new vocabulary member.
     */
    bySubscriptionState: tally(states.filter((s): s is SubscriptionState => s !== null)),
  };
}

/**
 * A figure a person WILL look for on this screen, and the reason it is not there.
 *
 * Written out rather than omitted. An Owner scanning a dashboard for revenue and finding nothing
 * concludes either "we have no revenue" or "this product cannot tell me" — and those need different
 * actions, so the screen says which.
 */
export interface UnavailableFigure {
  readonly titleHe: string;
  readonly whyHe: string;
}

export const FIGURES_NOT_AVAILABLE: readonly UnavailableFigure[] = [
  {
    titleHe: 'הכנסה חודשית חוזרת (MRR)',
    whyHe:
      'הקריאה שממנה נבנה המסך הזה מחזירה מצב מנוי ומכסות בלבד, ואינה מחזירה סכומים. אין בשרת ' +
      'קריאה שמחזירה הכנסה, ולכן אין כאן מספר — לא אפס ולא הערכה.',
  },
  {
    titleHe: 'הכנסה שנתית חוזרת (ARR)',
    whyHe:
      'נגזרת מ-MRR ומאותה סיבה אינה זמינה: ללא סכום חודשי אין מה להכפיל בשנים עשר, ' +
      'ומספר שנבנה מהערכה הוא מספר שאיש לא מדד.',
  },
  {
    titleHe: 'גבייה בפועל וחשבוניות',
    whyHe:
      'חיבור לספק הסליקה והחשבוניות אינו מוגדר במערכת. עד שיוגדר, אין מקור אמת לגבייה, וכל מספר ' +
      'שיוצג כאן יהיה מספר שאיש לא מדד.',
  },
];
